import type { TranslationKey } from '@/i18n/types'

export interface ApiError extends Error {
  readonly status: number
  readonly code?: string | undefined
  readonly field?: string | undefined
  readonly reason?: string | undefined
}

export interface ApiErrorBody {
  error?: string
  code?: string
  field?: string
  reason?: string
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && typeof (err as { status?: unknown }).status === 'number'
}

export const NOT_ASSIGNED_CODE = 'not_assigned'

export type AccessRefusal = 'notAssigned' | 'notFound'

export function isNotAssignedError(err: unknown): boolean {
  return isApiError(err) && err.status === 403 && err.code === NOT_ASSIGNED_CODE
}

export function accessRefusal(err: unknown): AccessRefusal | null {
  if (isNotAssignedError(err)) {
    return 'notAssigned'
  }
  if (isApiError(err) && err.status === 404) {
    return 'notFound'
  }
  return null
}

export function attachApiErrorFields(
  error: Error,
  status: number,
  body: ApiErrorBody | null
): void {
  const target = error as {
    message: string
    status?: number
    code?: string
    field?: string
    reason?: string
  }
  target.status = status
  if (!body || typeof body.error !== 'string' || !body.error) {
    target.message = ''
  } else {
    target.message = body.error
  }
  if (body?.code) {
    target.code = body.code
  }
  if (body?.field) {
    target.field = body.field
  }
  if (body?.reason) {
    target.reason = body.reason
  }
}

export function apiErrorMessage(err: unknown, t: (key: TranslationKey) => string): string {
  if (!isApiError(err) || err.status < 400 || err.status >= 500) {
    return t('common.error.title')
  }
  if (err.code === 'SESSION_EXPIRED') {
    return t('common.session.expired')
  }
  if (isNotAssignedError(err)) {
    return err.message.trim() || t('access.notAssigned.message')
  }
  const message = err.message.trim()
  return message || t('common.error.title')
}
